import { Loader2, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import * as api from "../api";

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const YEARS = Array.from({ length: 6 }, (_, i) => 2023 + i);

// ✅ Igual que en AddModal — usa item.type que ya viene mapeado del backend
function typeLabel(type) {
  return (
    {
      series: "📺 Serie",
      movie: "🎬 Película",
      documentary: "📽 Documental",
      anime: "⛩ Anime",
      short: "📎 Corto",
    }[type] || "🎬 Película"
  );
}

export default function SeriesCalendar({ onBack, onCardClick }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(
    YEARS.includes(currentYear) ? currentYear : 2026,
  );
  const [calendar, setCalendar] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addingMonth, setAddingMonth] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recent, setRecent] = useState([]);

  async function loadYear(y) {
    setLoading(true);
    setError("");
    try {
      const data = await api.getContent({ watched_year: y, status: "watched" });
      const grouped = {};
      for (let m = 1; m <= 12; m++) grouped[m] = [];
      data.forEach((item) => {
        if (item.watched_date) {
          // ✅ Parsear fecha evitando desfase de zona horaria
          const [, month] = item.watched_date.split("-");
          const m = parseInt(month, 10);
          if (grouped[m]) grouped[m].push(item);
        }
      });
      setCalendar(grouped);
    } catch (err) {
      setError("Error al cargar el calendario: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadYear(year);
  }, [year]);

  useEffect(() => {
    (async () => {
      try {
        const all = await api.getContent();
        setRecent(all.slice(0, 5));
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.getContent({ search: query });
        setResults(data.slice(0, 8));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  async function handleAdd(item) {
    const watched_date = `${year}-${String(addingMonth).padStart(2, "0")}-01`;
    setError("");
    try {
      // ✅ Dos llamadas separadas y simples — evita el 500
      await api.updateStatus(item.id, "watched");
      await api.updateContent(item.id, { watched_date });
      setAddingMonth(null);
      setQuery("");
      setResults([]);
      await loadYear(year);
    } catch (err) {
      setError("Error al añadir: " + err.message);
    }
  }

  async function handleRemove(item) {
    setError("");
    try {
      // ✅ Separar las dos operaciones
      await api.updateStatus(item.id, "pending");
      await api.updateContent(item.id, { watched_date: "NULL" });
      await loadYear(year);
    } catch (err) {
      setError("Error al eliminar: " + err.message);
    }
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-white">📅 Calendario visto</h2>
        <div className="flex items-center gap-3">
          <select
            className="bg-cinema-card border border-cinema-border rounded-xl px-4 py-2 text-white"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          {onBack && (
            <button
              onClick={onBack}
              className="bg-steel-700 hover:bg-steel-600 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all"
            >
              ← Volver
            </button>
          )}
        </div>
      </div>

      {recent.length > 0 && (
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wide text-steel-400">Añadido recientemente</span>
          <div className="flex items-center gap-1">
            {recent.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onCardClick && onCardClick(item)}
                className="group rounded-md border border-cinema-border overflow-hidden hover:border-cinema-accent/50 transition-colors"
                title={item.title}
                aria-label={`Abrir ${item.title}`}
              >
                {item.poster ? (
                  <img
                    src={item.poster}
                    alt={item.title}
                    className="w-8 h-12 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-8 h-12 bg-steel-700 flex items-center justify-center text-xs">
                    {item.type === "series" ? "📺" : "🎬"}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-900/80 border border-red-700 text-red-200 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError("")}>
            <X size={14} />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-steel-300">
          <Loader2 className="animate-spin" size={20} /> Cargando...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {MONTHS.map((monthName, idx) => {
            const monthNum = idx + 1;
            const items = calendar[monthNum] || [];
            return (
              <div
                key={monthNum}
                className="bg-cinema-card rounded-xl p-4 shadow-lg"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-white">
                    {monthName}
                  </h3>
                  <button
                    onClick={() => {
                      setAddingMonth(monthNum);
                      setQuery("");
                      setResults([]);
                    }}
                    className="flex items-center gap-1 bg-cinema-accent hover:bg-cinema-accent-hover text-white px-2.5 py-1.5 rounded-lg text-xs font-medium"
                  >
                    <Plus size={13} /> Añadir
                  </button>
                </div>
                <ul className="space-y-2">
                  {items.length > 0 ? (
                    items.map((item) => (
                      <li
                        key={item.id}
                        className="flex items-center gap-2 bg-steel-800/60 rounded-lg px-3 py-2"
                      >
                        {item.poster && (
                          <img
                            src={item.poster}
                            alt=""
                            className="w-7 h-10 object-cover rounded flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-medium truncate">
                            {item.title}
                          </p>
                          {/* ✅ typeLabel correcto */}
                          <p className="text-steel-400 text-xs">
                            {item.year} · {typeLabel(item.type)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemove(item)}
                          className="text-steel-500 hover:text-red-400 transition-colors flex-shrink-0"
                        >
                          <X size={13} />
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="text-steel-600 text-xs italic">
                      Sin títulos
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {addingMonth !== null && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAddingMonth(null);
          }}
        >
          <div className="bg-cinema-card w-full sm:max-w-md sm:rounded-xl rounded-t-2xl shadow-2xl flex flex-col max-h-[80vh]">
            <div className="flex-shrink-0 p-4 border-b border-cinema-border">
              <div className="w-10 h-1 bg-steel-600 rounded-full mx-auto mb-3 sm:hidden" />
              <div className="flex items-center justify-between">
                <p className="text-white font-semibold text-sm">
                  Añadir a{" "}
                  <span className="text-cinema-accent">
                    {MONTHS[addingMonth - 1]} {year}
                  </span>
                </p>
                <button
                  onClick={() => setAddingMonth(null)}
                  className="text-steel-400 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-steel-400 text-xs mt-1">
                Busca entre todos tus títulos
              </p>
              <div className="relative mt-3">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full bg-cinema-bg border border-cinema-border rounded-xl px-3 py-2.5 text-white text-sm placeholder-steel-500 focus:outline-none focus:border-cinema-accent"
                  placeholder="Buscar en tu lista..."
                  autoFocus
                />
                {searching && (
                  <Loader2
                    size={14}
                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-cinema-accent"
                  />
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {results.length > 0 ? (
                <ul className="space-y-2">
                  {results.map((item) => (
                    <li
                      key={item.id}
                      onClick={() => handleAdd(item)}
                      className="flex items-center gap-3 px-3 py-2.5 bg-cinema-bg border border-cinema-border rounded-xl hover:border-cinema-accent cursor-pointer transition-all"
                    >
                      {item.poster && (
                        <img
                          src={item.poster}
                          alt=""
                          className="w-8 h-12 object-cover rounded flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {item.title}
                        </p>
                        {/* ✅ typeLabel correcto en el buscador */}
                        <p className="text-steel-400 text-xs">
                          {item.year} · {typeLabel(item.type)}
                          {item.status === "watched" && " · ✅ Vista"}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : query && !searching ? (
                <p className="text-steel-500 text-sm text-center py-6">
                  Sin resultados en tu lista
                </p>
              ) : (
                <p className="text-steel-600 text-xs text-center py-6">
                  Escribe para buscar
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
